import { useEffect, useRef } from "react";

/**
 * Hook de polling: ejecuta `fn` cada `intervalMs` mientras el componente
 * esté montado y el tab del navegador esté visible (Page Visibility API).
 *
 * - Pausa cuando el usuario cambia de tab (no consume cuota innecesaria).
 * - Reanuda al volver al tab.
 * - Cancela en unmount.
 * - NO ejecuta `fn` en mount (eso lo hace el caller con su propio useEffect).
 *
 * Uso:
 *   usePolling(load, 15000, [load]);
 */
export function usePolling(fn, intervalMs, deps = []) {
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);

  useEffect(() => {
    let intervalId = null;

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        if (document.visibilityState === "visible") {
          try { fnRef.current(); } catch { /* ignore */ }
        }
      }, intervalMs);
    };

    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Refresh inmediato al volver al tab
        try { fnRef.current(); } catch { /* ignore */ }
        start();
      } else {
        stop();
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);
}
